import { getIcons, getImageByUrl, getPostImages, StoredImage } from './post-store';
import { imageKeyUrl } from './post-images';

/**
 * Shows stored images while offline. When active, every <img> on the page — React-rendered (feed
 * icons) or built by the post renderer — whose URL has a stored image gets that image as a blob: URL.
 * A MutationObserver catches images as they are added or their src changes.
 *
 * Swaps happen synchronously for images already in memory, before the browser's network attempt can
 * fail and trigger error fallbacks (FeedIcon replaces a failing icon). So all icons are loaded on
 * activation, and the post engine loads a post's images when it serves the post from storage
 * (loadPostImages). Anything else is looked up asynchronously.
 */

let activeUserId: string | undefined;
let observer: MutationObserver | undefined;
// imageKeyUrl → blob: URL. Kept until offline reading is switched off (releaseOfflineImages): a
// revoked URL would break images still on screen.
const objectUrls = new Map<string, string>();

function objectUrlFor(image: StoredImage): string {
  let url = objectUrls.get(image.url);
  if (!url) {
    url = URL.createObjectURL(new Blob([image.data], { type: image.type }));
    objectUrls.set(image.url, url);
  }
  return url;
}

/** Start (with the user whose images to use) or stop showing stored images. */
export async function setOfflineImageUser(userId: string | undefined): Promise<void> {
  if (userId == activeUserId) return;
  activeUserId = userId;
  observer?.disconnect();
  observer = undefined;
  if (!userId || typeof document == 'undefined' || typeof MutationObserver == 'undefined') return;

  (await getIcons(userId)).forEach(objectUrlFor);
  if (activeUserId != userId) return; // switched meanwhile
  observer = new MutationObserver(records => {
    for (const record of records) {
      if (record.type == 'attributes') {
        if (record.target instanceof HTMLImageElement) swap(record.target);
        continue;
      }
      record.addedNodes.forEach(node => {
        if (node instanceof HTMLImageElement) swap(node);
        else if (node instanceof Element) node.querySelectorAll('img').forEach(swap);
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
  document.querySelectorAll('img').forEach(swap);
}

/** Load a post's stored images into memory, so they swap in without delay when it renders. */
export async function loadPostImages(userId: string, feedId: string, postIndex: number): Promise<void> {
  if (userId != activeUserId) return;
  (await getPostImages(userId, feedId, postIndex)).forEach(objectUrlFor);
}

/** Free all blob: URLs (offline reading switched off, logout). */
export function releaseOfflineImages(): void {
  objectUrls.forEach(url => URL.revokeObjectURL(url));
  objectUrls.clear();
}

function swap(img: HTMLImageElement) {
  const src = img.getAttribute('src');
  if (!src || src.startsWith('blob:') || src.startsWith('data:')) return;
  let key: string;
  try { key = imageKeyUrl(new URL(src, document.baseURI).href); }
  catch { return; }

  const known = objectUrls.get(key);
  if (known) return apply(img, src, known);
  const userId = activeUserId;
  if (!userId) return;
  getImageByUrl(userId, key).then(image => {
    // Still offline, and the element still shows the same image?
    if (image && activeUserId == userId && img.getAttribute('src') == src) apply(img, src, objectUrlFor(image));
  });
}

function apply(img: HTMLImageElement, src: string, objectUrl: string) {
  img.dataset.offlineSrc = src;
  // A srcset (or <picture> sources) would win over src and point to the network again.
  img.removeAttribute('srcset');
  if (img.parentElement?.tagName == 'PICTURE')
    img.parentElement.querySelectorAll('source').forEach(source => source.removeAttribute('srcset'));
  img.src = objectUrl;
}
